import { useQuery } from "@tanstack/react-query";
import { ArrowRight, MapPinned, Siren } from "lucide-react";
import { useState } from "react";
import { PageHeader } from "../components/PageHeader";
import { RetryButton, StatePanel } from "../components/StatePanel";
import { StatusChip } from "../components/StatusChip";
import { api, formatDate } from "../lib/api";
import type { InspectionItem, ListResponse } from "../lib/types";

export function InspectionsPage() {
  const [state, setState] = useState("open");
  const [priority, setPriority] = useState("");
  const search = new URLSearchParams({ state });
  if (priority !== "") search.set("priority", priority);
  const queue = useQuery({
    queryKey: ["inspections", state, priority],
    queryFn: () =>
      api<ListResponse<InspectionItem>>(`/inspections?${search.toString()}`),
  });

  return (
    <>
      <PageHeader
        eyebrow="Field safeguarding"
        title="Inspection queue"
        description="Prioritize adverse planting evidence that requires human field follow-up within your assigned scope."
      />
      <div className="filter-bar inspection-filters">
        <label>
          Task state
          <select
            value={state}
            onChange={(event) => setState(event.target.value)}
          >
            <option value="open">Open</option>
            <option value="assigned">Assigned</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </label>
        <label>
          Priority
          <select
            value={priority}
            onChange={(event) => setPriority(event.target.value)}
          >
            <option value="">All priorities</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="normal">Normal</option>
          </select>
        </label>
      </div>
      {queue.isLoading ? (
        <StatePanel
          kind="loading"
          title="Loading inspection work"
          detail="Prioritizing adverse evidence within your active geographic scope."
        />
      ) : queue.isError || queue.data === undefined ? (
        <StatePanel
          kind="error"
          title="Inspection work is unavailable"
          detail="No task or review decision was changed."
          action={<RetryButton onClick={() => void queue.refetch()} />}
        />
      ) : queue.data.items.length === 0 ? (
        <StatePanel
          kind="empty"
          title="No inspection tasks"
          detail="There is no field follow-up in the current filter and assigned scope."
        />
      ) : (
        <section className="inspection-list" aria-label="Inspection queue">
          {queue.data.items.map((item) => (
            <article className="inspection-card" key={item.id}>
              <div className={`inspection-priority ${item.priority}`}>
                <Siren aria-hidden="true" />
                <span>{item.priority} priority</span>
              </div>
              <div className="inspection-heading">
                <div>
                  <span className="eyebrow">{item.planting_code}</span>
                  <h2>{item.plant_type}</h2>
                </div>
                <StatusChip state={item.condition} />
              </div>
              <p>{item.reason}</p>
              <dl className="inspection-facts">
                <div>
                  <dt>Site</dt>
                  <dd>
                    <MapPinned aria-hidden="true" />
                    {item.site_name}, {item.municipality_name}
                  </dd>
                </div>
                <div>
                  <dt>Evidence</dt>
                  <dd>
                    {item.post_type.replaceAll("_", " ")} ·{" "}
                    {item.observed_plant_count.toLocaleString()} observed
                  </dd>
                </div>
                <div>
                  <dt>Created</dt>
                  <dd>{formatDate(item.created_at)}</dd>
                </div>
                <div>
                  <dt>Risk signals</dt>
                  <dd>
                    {item.risk_flags === null
                      ? "Adverse field condition"
                      : item.risk_flags.replaceAll(",", ", ")}
                  </dd>
                </div>
              </dl>
              <a
                className="button secondary inspection-action"
                href={`/reviews?post=${encodeURIComponent(item.source_post_id)}`}
              >
                Open evidence review
                <ArrowRight aria-hidden="true" />
              </a>
            </article>
          ))}
        </section>
      )}
    </>
  );
}
